export const experienceTypes = {
  'passover-seder': {
    name: 'Passover Seder',
    icon: '🍷',
    description: 'Plan a meaningful Passover celebration',
    defaultDuration: '2-4 hours',
    exposureCategories: [
      {
        id: 'seder-styles',
        name: 'Seder Styles',
        options: [
          {
            id: '90-min-seder',
            icon: '⚡',
            title: 'The 90-Minute Seder',
            description: 'Modern, engaging, kid-friendly. All the meaning, none of the marathon. Perfect for families with young children.',
          },
          {
            id: 'traditional-seder',
            icon: '📖',
            title: 'Traditional 4-Hour Seder',
            description: 'Complete traditional haggadah reading. Deep dive into the Exodus story with all the prayers and songs.',
          },
          {
            id: 'theatrical-seder',
            icon: '🎭',
            title: 'Theatrical Seder',
            description: 'Kids act out the Exodus story. Props, costumes, improv. Educational meets entertainment.',
          },
        ],
      },
      {
        id: 'menu',
        name: 'Menu Approaches',
        options: [
          {
            id: 'fusion-menu',
            icon: '🍽️',
            title: 'Traditional + Modern Fusion',
            description: 'Classic brisket alongside modern Israeli dishes. Best of both worlds.',
          },
          {
            id: 'vegetarian-menu',
            icon: '🥗',
            title: 'Vegetarian Focus',
            description: 'Creative plant-based dishes that honor the holiday without meat.',
          },
          {
            id: 'traditional-menu',
            icon: '🍖',
            title: 'Traditional Menu',
            description: 'Classic brisket, matzo ball soup, and traditional sides.',
          },
        ],
      },
      {
        id: 'activities',
        name: 'Kids Activities',
        options: [
          {
            id: 'treasure-hunt',
            icon: '🔍',
            title: 'Afikoman Treasure Hunt',
            description: 'Create clues and hide the afikoman for kids to find.',
          },
          {
            id: 'crafts',
            icon: '🎨',
            title: 'Passover Crafts',
            description: 'Make seder plates, decorate placemats, create plagues puppets.',
          },
          {
            id: 'story-acting',
            icon: '🎬',
            title: 'Act Out the Story',
            description: 'Kids perform the Exodus story with props and costumes.',
          },
        ],
      },
    ],
    defaultDecisions: [
      { key: 'seder-style', title: 'Seder Style', status: 'research' },
      { key: 'basics', title: 'Basics', status: 'research' },
      { key: 'menu', title: 'Menu', status: 'research' },
      { key: 'activities', title: 'Activities for Kids', status: 'research' },
      { key: 'haggadah', title: 'Haggadah Choice', status: 'research' },
    ],
    defaultTimeline: [
      {
        id: '6-weeks',
        date: '6 Weeks Out',
        status: 'upcoming',
        tasks: [
          { id: 't1', text: 'Decide on seder style and format', completed: false },
          { id: 't2', text: 'Send invites to guests', completed: false },
        ],
      },
      {
        id: '4-weeks',
        date: '4 Weeks Out (This Week!)',
        status: 'active',
        tasks: [
          { id: 't3', text: 'Finalize menu', completed: false },
          { id: 't4', text: 'Order wine', completed: false },
          { id: 't5', text: 'Ask kids about activities they\'d enjoy', completed: false },
        ],
      },
      {
        id: '2-weeks',
        date: '2 Weeks Out',
        status: 'upcoming',
        tasks: [
          { id: 't6', text: 'Big grocery shop for non-perishables', completed: false },
          { id: 't7', text: 'Prep kid activities', completed: false },
          { id: 't8', text: 'Confirm final headcount', completed: false },
        ],
      },
      {
        id: 'week-of',
        date: 'Week Of',
        status: 'upcoming',
        tasks: [
          { id: 't9', text: 'Cook brisket (3 days before)', completed: false },
          { id: 't10', text: 'Fresh grocery shop', completed: false },
          { id: 't11', text: 'Set table and arrange seating', completed: false },
          { id: 't12', text: 'Prep seder plate and haggadahs', completed: false },
        ],
      },
    ],
    defaultBudget: 600,
  },
  'shabbat-dinners': {
    name: 'Shabbat Dinners',
    icon: '🕯️',
    description: 'Make Friday night special with weekly Shabbat dinners',
    recurring: true,
    exposureCategories: [
      {
        id: 'dinner-style',
        name: 'Dinner Style',
        options: [
          {
            id: 'casual-friday',
            icon: '🍕',
            title: 'Casual Friday Night',
            description: 'Simple, relaxed dinner. No pressure, just connection.',
          },
          {
            id: 'traditional-shabbat',
            icon: '🕯️',
            title: 'Traditional Shabbat',
            description: 'Candles, challah, blessings, and a special meal.',
          },
          {
            id: 'shabbat-saturday',
            icon: '🥞',
            title: 'Shabbat Saturday',
            description: 'Move it to Saturday morning - pancakes, phones away, family time.',
          },
        ],
      },
      {
        id: 'menu-approach',
        name: 'Menu Approach',
        options: [
          {
            id: '30-min-meals',
            icon: '⚡',
            title: '30-Minute Meals',
            description: 'Quick, delicious dinners that fit your schedule.',
          },
          {
            id: 'make-ahead',
            icon: '🍲',
            title: 'Make-Ahead Meals',
            description: 'Prep on Sunday, enjoy all week.',
          },
          {
            id: 'rotating-menu',
            icon: '🔄',
            title: 'Rotating Menu',
            description: 'A few favorite recipes you rotate through.',
          },
        ],
      },
    ],
    defaultDecisions: [
      { key: 'dinner-style', title: 'Dinner Style', status: 'research' },
      { key: 'menu-approach', title: 'Menu Approach', status: 'research' },
      { key: 'frequency', title: 'How Often?', status: 'research' },
      { key: 'rituals', title: 'Rituals & Blessings', status: 'research' },
    ],
    defaultTimeline: [
      {
        id: 'setup',
        date: 'Initial Setup',
        status: 'active',
        tasks: [
          { id: 't1', text: 'Choose your dinner style', completed: false },
          { id: 't2', text: 'Pick 3-5 go-to recipes', completed: false },
          { id: 't3', text: 'Set up your Friday routine', completed: false },
        ],
      },
    ],
    defaultBudget: 100,
  },
  'bar-bat-mitzvah': {
    name: 'Bar/Bat Mitzvah',
    icon: '📜',
    description: 'Create a meaningful coming-of-age celebration',
    defaultDuration: '12-18 months planning',
    exposureCategories: [
      {
        id: 'celebration-style',
        name: 'Celebration Style',
        options: [
          {
            id: 'intimate-family',
            icon: '👨‍👩‍👧‍👦',
            title: 'Intimate Family Celebration',
            description: 'Close family and friends, meaningful and personal.',
          },
          {
            id: 'community-party',
            icon: '🎉',
            title: 'Community Party',
            description: 'Big celebration with extended family and friends.',
          },
          {
            id: 'service-project',
            icon: '🤝',
            title: 'Service Project Focus',
            description: 'Celebrate through giving back and community service.',
          },
        ],
      },
      {
        id: 'learning-approach',
        name: 'Learning Approach',
        options: [
          {
            id: 'hebrew-tutoring',
            icon: '📚',
            title: 'Hebrew Tutoring',
            description: 'One-on-one or small group Hebrew learning.',
          },
          {
            id: 'family-learning',
            icon: '👨‍👩‍👧‍👦',
            title: 'Family Learning Together',
            description: 'Learn as a family, make it a shared journey.',
          },
          {
            id: 'online-program',
            icon: '💻',
            title: 'Online Program',
            description: 'Flexible online Hebrew and Jewish learning.',
          },
        ],
      },
    ],
    defaultDecisions: [
      { key: 'celebration-style', title: 'Celebration Style', status: 'research' },
      { key: 'learning-approach', title: 'Learning Approach', status: 'research' },
      { key: 'date', title: 'Date & Venue', status: 'research' },
      { key: 'guest-list', title: 'Guest List', status: 'research' },
      { key: 'service', title: 'Service or Ceremony', status: 'research' },
    ],
    defaultTimeline: [
      {
        id: '12-months',
        date: '12 Months Out',
        status: 'upcoming',
        tasks: [
          { id: 't1', text: 'Choose celebration style', completed: false },
          { id: 't2', text: 'Select learning approach', completed: false },
          { id: 't3', text: 'Set date and book venue', completed: false },
        ],
      },
      {
        id: '6-months',
        date: '6 Months Out',
        status: 'upcoming',
        tasks: [
          { id: 't4', text: 'Begin Hebrew learning', completed: false },
          { id: 't5', text: 'Finalize guest list', completed: false },
          { id: 't6', text: 'Plan ceremony details', completed: false },
        ],
      },
      {
        id: '3-months',
        date: '3 Months Out',
        status: 'upcoming',
        tasks: [
          { id: 't7', text: 'Send invitations', completed: false },
          { id: 't8', text: 'Plan menu and catering', completed: false },
          { id: 't9', text: 'Order decorations', completed: false },
        ],
      },
    ],
    defaultBudget: 5000,
  },
  'rosh-hashanah': {
    name: 'Rosh Hashanah',
    icon: '🍎',
    description: 'Welcome the Jewish New Year with meaning',
    defaultDuration: '2-3 hours',
    exposureCategories: [
      {
        id: 'celebration-style',
        name: 'Celebration Style',
        options: [
          {
            id: 'family-dinner',
            icon: '🍽️',
            title: 'Family Dinner',
            description: 'Intimate dinner with traditional foods and reflection.',
          },
          {
            id: 'community-meal',
            icon: '👥',
            title: 'Community Meal',
            description: 'Gather with friends and extended family.',
          },
          {
            id: 'outdoor-celebration',
            icon: '🌳',
            title: 'Outdoor Tashlich',
            description: 'Combine dinner with tashlich ceremony by water.',
          },
        ],
      },
      {
        id: 'menu',
        name: 'Menu',
        options: [
          {
            id: 'traditional-foods',
            icon: '🍯',
            title: 'Traditional Foods',
            description: 'Apples and honey, round challah, pomegranates.',
          },
          {
            id: 'modern-twist',
            icon: '✨',
            title: 'Modern Twist',
            description: 'Traditional flavors with contemporary presentation.',
          },
        ],
      },
    ],
    defaultDecisions: [
      { key: 'celebration-style', title: 'Celebration Style', status: 'research' },
      { key: 'menu', title: 'Menu', status: 'research' },
      { key: 'rituals', title: 'Rituals & Reflection', status: 'research' },
    ],
    defaultTimeline: [
      {
        id: '2-weeks',
        date: '2 Weeks Out',
        status: 'upcoming',
        tasks: [
          { id: 't1', text: 'Plan menu', completed: false },
          { id: 't2', text: 'Send invites', completed: false },
        ],
      },
      {
        id: 'week-of',
        date: 'Week Of',
        status: 'upcoming',
        tasks: [
          { id: 't3', text: 'Shop for ingredients', completed: false },
          { id: 't4', text: 'Prepare traditional foods', completed: false },
        ],
      },
    ],
    defaultBudget: 300,
  },
  'hanukkah': {
    name: 'Hanukkah',
    icon: '🕎',
    description: 'Eight nights of light and celebration',
    defaultDuration: '8 nights',
    exposureCategories: [
      {
        id: 'celebration-approach',
        name: 'Celebration Approach',
        options: [
          {
            id: 'nightly-lighting',
            icon: '🕯️',
            title: 'Nightly Candle Lighting',
            description: 'Light candles each night with family.',
          },
          {
            id: 'big-party',
            icon: '🎉',
            title: 'One Big Party',
            description: 'Celebrate one night with extended family and friends.',
          },
          {
            id: 'progressive-celebration',
            icon: '📅',
            title: 'Progressive Celebration',
            description: 'Different activity each night.',
          },
        ],
      },
      {
        id: 'activities',
        name: 'Activities',
        options: [
          {
            id: 'dreidel-games',
            icon: '🎲',
            title: 'Dreidel Games',
            description: 'Classic dreidel with chocolate coins.',
          },
          {
            id: 'latke-party',
            icon: '🥔',
            title: 'Latke Making Party',
            description: 'Kids help make latkes together.',
          },
          {
            id: 'gift-tradition',
            icon: '🎁',
            title: 'Gift Tradition',
            description: 'Small gifts each night or one big gift.',
          },
        ],
      },
    ],
    defaultDecisions: [
      { key: 'celebration-approach', title: 'Celebration Approach', status: 'research' },
      { key: 'activities', title: 'Activities', status: 'research' },
      { key: 'gifts', title: 'Gift Tradition', status: 'research' },
    ],
    defaultTimeline: [
      {
        id: '2-weeks',
        date: '2 Weeks Before',
        status: 'upcoming',
        tasks: [
          { id: 't1', text: 'Get menorah and candles', completed: false },
          { id: 't2', text: 'Plan activities for each night', completed: false },
        ],
      },
    ],
    defaultBudget: 200,
  },
  'purim': {
    name: 'Purim',
    icon: '🎭',
    description: 'Celebrate with costumes, stories, and joy',
    defaultDuration: '2-3 hours',
    exposureCategories: [
      {
        id: 'celebration-style',
        name: 'Celebration Style',
        options: [
          {
            id: 'costume-party',
            icon: '🎭',
            title: 'Costume Party',
            description: 'Dress up and act out the Purim story.',
          },
          {
            id: 'megillah-reading',
            icon: '📜',
            title: 'Megillah Reading',
            description: 'Read or tell the story of Esther.',
          },
          {
            id: 'carnival',
            icon: '🎪',
            title: 'Purim Carnival',
            description: 'Games, activities, and treats for kids.',
          },
        ],
      },
      {
        id: 'treats',
        name: 'Hamantaschen & Treats',
        options: [
          {
            id: 'homemade',
            icon: '👩‍🍳',
            title: 'Homemade Hamantaschen',
            description: 'Bake together as a family activity.',
          },
          {
            id: 'bakery',
            icon: '🍪',
            title: 'Order from Bakery',
            description: 'Support a local Jewish bakery.',
          },
        ],
      },
    ],
    defaultDecisions: [
      { key: 'celebration-style', title: 'Celebration Style', status: 'research' },
      { key: 'costumes', title: 'Costumes', status: 'research' },
      { key: 'treats', title: 'Hamantaschen & Treats', status: 'research' },
    ],
    defaultTimeline: [
      {
        id: '2-weeks',
        date: '2 Weeks Before',
        status: 'upcoming',
        tasks: [
          { id: 't1', text: 'Plan costumes', completed: false },
          { id: 't2', text: 'Get hamantaschen or ingredients', completed: false },
        ],
      },
    ],
    defaultBudget: 150,
  },
};

export const getExperienceType = (type) => {
  return experienceTypes[type] || experienceTypes['passover-seder'];
};

export const getAllExperienceTypes = () => {
  return Object.entries(experienceTypes).map(([key, value]) => ({
    id: key,
    ...value,
  }));
};

